import { TEST_ID_RECOMMENDATION } from "e2e/testId";
import { CheckIcon, CircleXIcon, PlusIcon } from "lucide-react-native";
import { RefObject, memo } from "react";
import { Pressable } from "@/components/atoms/Pressable";

import AddToWatchlistActionsheet from "@/components/organisms/AddToWatchlistActionsheet";
import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";

import { RecommendationsPlayerFragment } from "@/generated/graphql";

import RankChangeIndicator from "@/features/recommendations/components/molecules/RankChangeIndicator";

/**
 * Props interface for InfoRecommendation view component
 */
interface IInfoRecommendation {
  rankChange: number;
  playerName: string;
  isDeleting?: boolean;
  loading?: boolean;
  isPlayerInAnyWatchList: boolean;
  positionAndTeamName: string;
  onOpenPlayerDetail: () => void;
  onPressPlayer: () => void;
  onAddPress: () => void;
  player: RecommendationsPlayerFragment;
  showActionsheet?: boolean;
  isPlayerInAnyWatchlist?: boolean;
  promptRef?: RefObject<CustomPromptRefProps>;
  isSmallScreen: boolean;
  onAddWatchlistPress: () => void;
  onCloseActionSheet: () => void;
  onRemovePlayer?: () => void;
  isDark: boolean;
}
/**
 * View component for recommendation info card with player details and actions
 * @remarks
 * Displays player recommendation with rank change, actions, and watchlist sheet
 * @param root0 - Component props
 * @param root0.player - Player recommendations fragment with basic player data
 * @param root0.showActionsheet - Whether add-to-watchlist action sheet is visible
 * @param root0.isPlayerInAnyWatchlist - Whether player exists in any watchlist
 * @param root0.promptRef - Ref to custom prompt component for confirmation dialogs
 * @param root0.onAddWatchlistPress - Callback fired when adding player to watchlist
 * @param root0.onCloseActionSheet - Callback fired when closing action sheet
 * @param root0.loading - Whether watchlist operations are in progress
 * @param root0.rankChange - Player's rank change value (positive/negative/zero)
 * @param root0.playerName - Formatted player name for display
 * @param root0.isDeleting - Whether player is being removed from recommendations
 * @param root0.positionAndTeamName - Formatted position and team name string
 * @param root0.isPlayerInAnyWatchList - Whether player is in any watchlist (duplicate of isPlayerInAnyWatchlist)
 * @param root0.isSmallScreen - Whether screen size is mobile/small
 * @param root0.onAddPress - Callback fired when add button is pressed
 * @param root0.onPressPlayer - Callback fired when player card is pressed
 * @param root0.onOpenPlayerDetail - Callback fired to navigate to player detail
 * @param root0.onRemovePlayer - Callback fired when removing player from recommendations
 * @param root0.isDark - Whether dark mode is active
 */
const InfoRecommendationView = ({
  player,
  showActionsheet,
  isPlayerInAnyWatchlist,
  promptRef,
  onAddWatchlistPress,
  onCloseActionSheet,
  loading,
  rankChange,
  playerName,
  isDeleting,
  positionAndTeamName,
  isPlayerInAnyWatchList,
  isSmallScreen,
  onAddPress,
  onPressPlayer,
  onOpenPlayerDetail,
  onRemovePlayer,
  isDark,
}: IInfoRecommendation) => (
  <Pressable
    testID={TEST_ID_RECOMMENDATION.PLAYER_ITEM}
    onPress={isSmallScreen ? onOpenPlayerDetail : onPressPlayer}
    disabled={isDeleting}
    className="rounded-sm border border-outline-subtle bg-surface-card px-3 py-2"
  >
    <Stack direction="horizontal" className="items-center">
      {!isSmallScreen && (
        <Pressable
          testID={TEST_ID_RECOMMENDATION.ADD_TO_LIST_BUTTON}
          className={`size-6 items-center justify-center rounded-full ${isPlayerInAnyWatchList ? "bg-accent-primary" : `${isDark ? "bg-surface-action" : "bg-surface-muted"}`}`}
          onPress={loading ? undefined : onAddPress}
        >
          <Icon
            testID={
              isPlayerInAnyWatchList
                ? TEST_ID_RECOMMENDATION.PLAYER_CHECKED_ICON
                : TEST_ID_RECOMMENDATION.PLAYER_UNCHECKED_ICON
            }
            as={isPlayerInAnyWatchList ? CheckIcon : PlusIcon}
            size="sm"
            className={
              isPlayerInAnyWatchList
                ? "text-content-on-accent"
                : "text-accent-secondary"
            }
            // @ts-expect-error -- strokeWidth passes through to the SVG but is not in the Icon atom API
            strokeWidth={3}
          />
        </Pressable>
      )}
      <Stack className={`min-w-0 flex-1 ${!isSmallScreen ? "pl-2" : ""}`}>
        <Text
          variant="body"
          className={`truncate ${isDark ? "text-white" : "text-content-primary"}`}
        >
          {isDeleting ? "Please wait..." : playerName}
        </Text>
        <Text variant="body" className="truncate text-content-subtle">
          {positionAndTeamName}
        </Text>
      </Stack>
      <Stack direction="horizontal" className="items-center gap-3">
        <RankChangeIndicator rankChange={rankChange} />
        {!isSmallScreen && (
          <Pressable onPress={onRemovePlayer} className="shrink-0">
            <Icon as={CircleXIcon} size="md" className="text-status-error" />
          </Pressable>
        )}
      </Stack>
    </Stack>
    {showActionsheet && player && (
      <AddToWatchlistActionsheet
        item={player}
        promptRef={promptRef}
        showActionsheet={showActionsheet}
        loading={!!isDeleting}
        isPlayerInAnyWatchlist={!!isPlayerInAnyWatchlist}
        onAddPress={onAddPress}
        onClose={onCloseActionSheet}
        onAddWatchlistPress={onAddWatchlistPress}
        isDark={isDark}
      />
    )}
  </Pressable>
);
InfoRecommendationView.displayName = "InfoRecommendationView";
export default memo(InfoRecommendationView);
